/**
 * Centralized Logging Service
 *
 * Provides structured logging with multiple severity levels, production filtering,
 * and optional remote logging capabilities.
 *
 * @module services/logger
 */

/**
 * Log severity levels
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/**
 * Log entry structure for remote logging
 */
export interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  data?: unknown
  error?: SerializedError
  context?: Record<string, unknown>
}

/**
 * Serialized error structure
 */
export interface SerializedError {
  name: string
  message: string
  stack?: string
  cause?: unknown
}

/**
 * Logger configuration options
 */
export interface LoggerConfig {
  /**
   * Minimum log level to output
   * Logs below this level will be suppressed
   */
  minLevel: LogLevel

  /**
   * Whether to include timestamps in console output
   */
  includeTimestamp: boolean

  /**
   * Enable remote logging
   */
  enableRemote: boolean

  /**
   * Remote logging endpoint URL
   */
  remoteEndpoint?: string

  /**
   * Additional context to include with every log
   */
  globalContext?: Record<string, unknown>
}

/**
 * Default logger configuration
 */
const DEFAULT_CONFIG: LoggerConfig = {
  minLevel: import.meta.env.PROD ? 'info' : 'debug',
  includeTimestamp: true,
  enableRemote: false,
  globalContext: {
    app: 'Strive',
    version: import.meta.env.VITE_APP_VERSION ?? 'unknown',
  },
}

/**
 * Log level priority mapping for filtering
 */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

/**
 * Centralized Logger Class
 *
 * Provides structured logging with configurable levels and output destinations.
 * Automatically filters logs based on minimum level and environment.
 */
class Logger {
  private config: LoggerConfig = DEFAULT_CONFIG
  private logBuffer: LogEntry[] = []
  private readonly maxBufferSize = 100

  /**
   * Configure the logger
   * @param config - Partial configuration to merge with defaults
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * Check if a log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.config.minLevel]
  }

  /**
   * Serialize an error for logging
   */
  private serializeError(error: unknown): SerializedError {
    if (error instanceof Error) {
      const serialized: SerializedError = {
        name: error.name,
        message: error.message,
      }

      if (error.stack !== undefined) {
        serialized.stack = error.stack
      }

      if (error.cause !== undefined) {
        serialized.cause = error.cause
      }

      return serialized
    }

    return {
      name: 'UnknownError',
      message: String(error),
    }
  }

  /**
   * Create a log entry
   */
  private createLogEntry(level: LogLevel, message: string, data?: unknown): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
    }

    if (this.config.globalContext !== undefined) {
      entry.context = this.config.globalContext
    }

    // For error level, always serialize the data as an error
    if (level === 'error') {
      entry.error = this.serializeError(data)
    } else if (data !== undefined) {
      // For other levels, treat data as regular data
      entry.data = data
    }

    return entry
  }

  /**
   * Format a log entry for console output
   */
  private formatConsoleMessage(entry: LogEntry): string {
    const parts: string[] = []

    if (this.config.includeTimestamp) {
      const timestamp = new Date(entry.timestamp).toLocaleTimeString()
      parts.push(`[${timestamp}]`)
    }

    parts.push(`[${entry.level.toUpperCase()}]`)
    parts.push(entry.message)

    return parts.join(' ')
  }

  /**
   * Output a log entry to the console
   */
  private outputToConsole(entry: LogEntry): void {
    const message = this.formatConsoleMessage(entry)

    switch (entry.level) {
      case 'debug':
        console.debug(message, entry.data ?? '')
        break
      case 'info':
        console.info(message, entry.data ?? '')
        break
      case 'warn':
        console.warn(message, entry.data ?? '', entry.error ?? '')
        break
      case 'error':
        console.error(message, entry.data ?? '', entry.error ?? '')
        if (entry.error?.stack != null) {
          console.error(entry.error.stack)
        }
        break
    }
  }

  /**
   * Send log entry to remote endpoint
   */
  private async sendToRemote(entry: LogEntry): Promise<void> {
    if (!this.config.enableRemote || this.config.remoteEndpoint == null) {
      return
    }

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      })
    } catch (error) {
      // Silently fail remote logging to avoid infinite loops
      console.error('Failed to send log to remote endpoint:', error)
    }
  }

  /**
   * Add log entry to buffer
   */
  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry)

    // Maintain buffer size limit
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift()
    }
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, data?: unknown): void {
    if (!this.shouldLog(level)) {
      return
    }

    const entry = this.createLogEntry(level, message, data)

    this.outputToConsole(entry)
    this.addToBuffer(entry)

    if (this.config.enableRemote) {
      void this.sendToRemote(entry)
    }
  }

  /**
   * Log a debug message
   * Only output in development mode unless explicitly configured
   *
   * @param message - Debug message
   * @param data - Optional additional data
   *
   * @example
   * logger.debug('User data loaded', { userId: '123', count: 10 })
   */
  debug(message: string, data?: unknown): void {
    this.log('debug', message, data)
  }

  /**
   * Log an informational message
   *
   * @param message - Info message
   * @param data - Optional additional data
   *
   * @example
   * logger.info('Navigation initialized', { mode: 'rolling' })
   */
  info(message: string, data?: unknown): void {
    this.log('info', message, data)
  }

  /**
   * Log a warning message
   *
   * @param message - Warning message
   * @param data - Optional additional data
   *
   * @example
   * logger.warn('Storage quota exceeded', { used: 0.95 })
   */
  warn(message: string, data?: unknown): void {
    this.log('warn', message, data)
  }

  /**
   * Log an error message
   *
   * @param message - Error message
   * @param error - Error object or additional data
   *
   * @example
   * logger.error('Failed to save data', new Error('Storage full'))
   */
  error(message: string, error?: unknown): void {
    this.log('error', message, error)
  }

  /**
   * Get recent logs from buffer
   * Useful for debugging or error reporting
   *
   * @param count - Number of recent logs to retrieve (default: all)
   * @returns Array of recent log entries
   */
  getRecentLogs(count?: number): LogEntry[] {
    if (count === undefined) {
      return [...this.logBuffer]
    }
    return this.logBuffer.slice(-count)
  }

  /**
   * Clear the log buffer
   */
  clearLogs(): void {
    this.logBuffer = []
  }

  /**
   * Get current logger configuration
   */
  getConfig(): Readonly<LoggerConfig> {
    return { ...this.config }
  }
}

/**
 * Singleton logger instance
 * Use this throughout the application for consistent logging
 *
 * @example
 * import { logger } from '@/services/logger'
 *
 * logger.info('App initialized')
 * logger.error('Failed to load data', error)
 */
export const logger = new Logger()

/**
 * Create a logger with additional context
 * Useful for module-specific or feature-specific logging
 *
 * @param context - Additional context to include with all logs
 * @returns Logger instance with merged context
 *
 * @example
 * const authLogger = createLogger({ module: 'auth' })
 * authLogger.info('User logged in', { userId: '123' })
 */
export function createLogger(context: Record<string, unknown>): Logger {
  const newLogger = new Logger()
  const config = logger.getConfig()

  newLogger.configure({
    ...config,
    globalContext: {
      ...config.globalContext,
      ...context,
    },
  })

  return newLogger
}
